import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import GradientBlue from "../../Components/InputFields/GradientBlue";
import Simple from "../../Components/InputFields/Simple";
import Spinner from "../../Components/Loader/Spinner";
import LogoWhite10 from "../../Media/PNG/LogoWhite10.svg";
import LogoBlue from "../../Media/PNG/LogoBlue.svg";
import { API_BASE_URL } from "../../config/api";
import Input from "./Components/Input";
import BirthDatePicker from "./Components/BirthDatePicker";
import Back from "./Components/Back";
import { GOOGLE_CREDENTIAL_STORAGE_KEY } from "./Components/GoogleAuthButton";

const DEFAULT_SIGNUP_DATA = {
  phone_number: "",
  fn: "",
  ln: "",
  national_id: "",
  birth_date: "",
  referral_code: "",
};

const PERSIAN_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
const ARABIC_DIGITS = "٠١٢٣٤٥٦٧٨٩";

function toEnglishDigits(value) {
  return String(value || "")
    .replace(/[۰-۹]/g, (digit) => String(PERSIAN_DIGITS.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String(ARABIC_DIGITS.indexOf(digit)));
}

function normalizeNationalId(value) {
  return toEnglishDigits(value).replace(/\D/g, "").slice(0, 10);
}

function isValidNationalId(value) {
  const nationalId = normalizeNationalId(value);

  if (!nationalId) return true;
  return /^\d{10}$/.test(nationalId);
}

function omitFalsyFields(payload) {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => Boolean(value)),
  );
}

function getCredential(search) {
  const params = new URLSearchParams(search);
  return (
    params.get("credential") ||
    params.get("credentials") ||
    sessionStorage.getItem(GOOGLE_CREDENTIAL_STORAGE_KEY) ||
    ""
  );
}

function getResponseData(result) {
  return result?.data || result || {};
}

function getErrorText(errorData) {
  if (!errorData) {
    return "درخواست انجام نشد. لطفاً دوباره تلاش کنید.";
  }

  if (typeof errorData.detail === "string") {
    return errorData.detail;
  }

  if (typeof errorData.message === "string") {
    return errorData.message;
  }

  const firstError = Object.values(errorData).find((value) => {
    if (Array.isArray(value)) return value.length > 0;
    return Boolean(value);
  });

  if (Array.isArray(firstError)) {
    return firstError[0];
  }

  if (typeof firstError === "string") {
    return firstError;
  }

  return "درخواست انجام نشد. لطفاً دوباره تلاش کنید.";
}

function storeAuthData(data) {
  localStorage.setItem("refresh", data.refresh);
  localStorage.setItem("access", data.access);
  localStorage.setItem("role", data.role);
  localStorage.setItem("email", data.email);

  if (data.uuid) {
    localStorage.setItem("uuid", data.uuid);
  }
  if (data.username) {
    localStorage.setItem("username", data.username);
  }

  localStorage.removeItem("authCredentials");
  sessionStorage.removeItem(GOOGLE_CREDENTIAL_STORAGE_KEY);
}

async function postGoogleLogin(payload) {
  const response = await fetch(`${API_BASE_URL}auth/trader/login/google/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  const result = await response.json().catch(() => ({}));
  return { response, result, data: getResponseData(result) };
}

function PageFrame({ children }) {
  return (
    <div className="relative flex justify-center items-center min-h-dvh bg-zi-bg1-light dark:bg-zi-bg1-dark">
      {children}
      <span className="hidden absolute w-full h-[1px] bottom-16 bg-zi-gradient-divider"></span>
    </div>
  );
}

function LoadingCard() {
  const [isDark] = useState(
    typeof document !== "undefined" &&
      document.documentElement.classList.contains("dark"),
  );
  const logoSrc = isDark ? LogoWhite10 : LogoBlue;

  return (
    <div className="w-full max-w-md bg-zi-bg2-light dark:bg-zi-bg2-dark rounded-2xl p-5 m-4 gap-7 flex flex-col items-center border border-zi-blue-light">
      <img src={logoSrc} alt="لوگو" className="w-14" />
      <Spinner color="#1f8fff" />
      <p className="font-light text-sm text-zi-text-light dark:text-zi-text-dark">
        در حال بررسی ورود با گوگل
      </p>
    </div>
  );
}

function GoogleTwoFaStep({ credential, onAuthSuccess }) {
  const [isDark] = useState(
    typeof document !== "undefined" &&
      document.documentElement.classList.contains("dark"),
  );
  const logoSrc = isDark ? LogoWhite10 : LogoBlue;
  const [inputNums, setInputNums] = useState(["", "", "", "", "", ""]);
  const [codeInvalid, setCodeInvalid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRefs = useRef([]);
  const submitInFlightRef = useRef(false);

  const handlePaste = (event, startIndex) => {
    event.preventDefault();
    const pasted = event.clipboardData?.getData("text") ?? "";
    const digits = pasted.replace(/[^0-9]/g, "");
    if (!digits) return;

    const trimmedDigits = digits.slice(0, inputNums.length - startIndex);
    setCodeInvalid(false);
    setInputNums((prev) => {
      const updated = [...prev];
      for (let offset = 0; offset < trimmedDigits.length; offset += 1) {
        updated[startIndex + offset] = trimmedDigits[offset];
      }
      return updated;
    });

    const nextIndex = startIndex + trimmedDigits.length;
    if (nextIndex < inputRefs.current.length) {
      inputRefs.current[nextIndex]?.focus();
    }
  };

  const handleSubmit = useCallback(async (event) => {
    if (event) {
      event.preventDefault();
    }
    if (submitInFlightRef.current) {
      return;
    }

    const otp = inputNums.join("");
    if (otp.length < 6) {
      setCodeInvalid(true);
      return;
    }

    submitInFlightRef.current = true;
    setIsSubmitting(true);
    setCodeInvalid(false);

    try {
      const { response, data } = await postGoogleLogin({
        credential,
        credentials: credential,
        otp,
      });

      if (!response.ok || !data.auth || !data.access || !data.refresh) {
        setCodeInvalid(true);
        return;
      }

      onAuthSuccess(data);
    } catch (error) {
      console.error("Google 2FA request failed:", error);
      setCodeInvalid(true);
    } finally {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  }, [credential, inputNums, onAuthSuccess]);

  useEffect(() => {
    if (inputNums.every((num) => num !== "")) {
      handleSubmit();
    }
  }, [handleSubmit, inputNums]);

  return (
    <div className="w-full max-w-md bg-zi-bg2-light dark:bg-zi-bg2-dark rounded-2xl p-5 m-4 gap-7 flex flex-col border border-zi-blue-light">
      <div className="flex flex-col gap-2 justify-center items-center">
        <img src={logoSrc} alt="لوگو" className="w-14" />
        <p className="font-medium text-sm text-zi-text-light dark:text-zi-text-dark">
          کوژیتو فاند
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center">
          <h1 className="text-lg font-medium text-zi-text-light dark:text-zi-text-dark">
            کد دو عاملی را وارد کنید
          </h1>
          <Back />
        </div>
        <span className="text-xs font-light text-zi-text-light/50 dark:text-zi-text-dark/50">
          کدی که در Google Authenticator می‌بینید را وارد کنید.
        </span>
      </div>

      <form className="flex flex-col gap-4 items-end" onSubmit={handleSubmit}>
        <div className="flex justify-between items-center gap-4 [direction:ltr]">
          {Array.from({ length: 6 }).map((_, i) => (
            <input
              key={i}
              ref={(element) => {
                inputRefs.current[i] = element;
              }}
              type="text"
              maxLength="1"
              inputMode="numeric"
              pattern="[0-9]*"
              value={inputNums[i]}
              className="size-full aspect-square rounded-lg caret-zi-blue-light border border-zi-border-light dark:border-zi-border-dark bg-zi-bg2-light dark:bg-zi-bg2-dark hocus:bg-zi-bg1-light hocus:dark:bg-zi-bg1-dark text-center text-xl font-medium text-zi-text-light dark:text-zi-text-dark outline-none hocus:ring-1 hocus:ring-zi-border-light hocus:dark:ring-zi-border-dark [direction:ltr]"
              onChange={(event) => {
                const value = event.target.value.replace(/[^0-9]/g, "");
                setCodeInvalid(false);
                setInputNums((prev) => {
                  const next = [...prev];
                  next[i] = value;
                  return next;
                });
                if (value) {
                  inputRefs.current[i + 1]?.focus();
                }
              }}
              onPaste={(event) => handlePaste(event, i)}
              onKeyDown={(event) => {
                if (event.key === "Backspace" && !event.target.value) {
                  inputRefs.current[i - 1]?.focus();
                }
                if (event.key === "ArrowLeft") {
                  event.preventDefault();
                  inputRefs.current[i - 1]?.focus();
                }
                if (event.key === "ArrowRight") {
                  event.preventDefault();
                  inputRefs.current[i + 1]?.focus();
                }
              }}
            />
          ))}
        </div>

        <button
          type="button"
          className="text-zi-blue-light text-xs hocus:underline bg-transparent"
        >
          به کد دو عاملی دسترسی ندارید؟
        </button>
      </form>
      {codeInvalid && (
        <p className="text-red-500 text-sm">کد وارد شده صحیح نمی‌باشد</p>
      )}

      <GradientBlue
        type="submit"
        value="ورود"
        isLoader={isSubmitting}
        onClick={handleSubmit}
      />
    </div>
  );
}

function GooglePhoneStep({ signupData, setSignupData, onNext }) {
  const [isDark] = useState(
    typeof document !== "undefined" &&
      document.documentElement.classList.contains("dark"),
  );
  const logoSrc = isDark ? LogoWhite10 : LogoBlue;
  const [fieldsRequired, setFieldsRequired] = useState(false);
  const [invalidData, setInvalidData] = useState(false);

  const handlePhoneChange = (event) => {
    setSignupData((previousData) => ({
      ...previousData,
      phone_number: toEnglishDigits(event.target.value).replace(/\D/g, ""),
    }));
    setFieldsRequired(false);
    setInvalidData(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!signupData.phone_number.trim()) {
      setFieldsRequired(true);
      return;
    }

    if (!/^09\d{9}$/.test(signupData.phone_number)) {
      setInvalidData(true);
      return;
    }

    onNext();
  };

  return (
    <div className="w-full max-w-md bg-zi-bg2-light dark:bg-zi-bg2-dark rounded-2xl p-5 m-4 gap-7 flex flex-col border border-zi-blue-light">
      <div className="flex flex-col gap-2 justify-center items-center">
        <img src={logoSrc} alt="لوگو" className="w-14" />
        <p className="font-medium text-sm text-zi-text-light dark:text-zi-text-dark">
          کوژیتو فاند
        </p>
      </div>
      <div className="flex justify-between items-center">
        <h1 className="text-lg font-medium text-zi-text-light dark:text-zi-text-dark">
          ایجاد حساب کاربری
        </h1>
      </div>

      <div>
        <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
          <Simple
            placeholder="شماره تلفن همراه"
            type="tel"
            id="phoneNumber"
            isNumeric={true}
            formValue={signupData.phone_number}
            formOnChange={handlePhoneChange}
          />
          <GradientBlue
            type="submit"
            value="مرحله بعدی"
            onClick={handleSubmit}
          />
        </form>
        {fieldsRequired && (
          <p className="text-red-500 text-sm font-extralight mt-1">
            لطفا تمامی فیلدها را پر کنید
          </p>
        )}
        {invalidData && (
          <p className="text-red-500 text-sm font-extralight mt-1">
            شماره تلفن همراه وارد شده معتبر نیست
          </p>
        )}
      </div>
    </div>
  );
}

function GoogleInfoStep({ credential, signupData, setSignupData, onBack, onAuthSuccess }) {
  const [isDark] = useState(
    typeof document !== "undefined" &&
      document.documentElement.classList.contains("dark"),
  );
  const logoSrc = isDark ? LogoWhite10 : LogoBlue;
  const [fieldsRequired, setFieldsRequired] = useState(false);
  const [invalidNationalId, setInvalidNationalId] = useState(false);
  const [signupError, setSignupError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (field) => (event) => {
    const value =
      field === "national_id"
        ? normalizeNationalId(event.target.value)
        : event.target.value;
    setSignupData((previousData) => ({ ...previousData, [field]: value }));
    setFieldsRequired(false);
    setInvalidNationalId(false);
    setSignupError("");
  };

  const handleBirthDateChange = (birthDate) => {
    setSignupData((previousData) => ({
      ...previousData,
      birth_date: birthDate,
    }));
    setSignupError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedFn = (signupData.fn || "").trim();
    const trimmedLn = (signupData.ln || "").trim();
    const normalizedNationalId = normalizeNationalId(signupData.national_id);

    if (!trimmedFn || !trimmedLn) {
      setFieldsRequired(true);
      return;
    }

    if (!isValidNationalId(normalizedNationalId)) {
      setInvalidNationalId(true);
      return;
    }

    const requestPayload = omitFalsyFields({
      credential,
      credentials: credential,
      phone_number: signupData.phone_number,
      fn: trimmedFn,
      ln: trimmedLn,
      national_id: normalizedNationalId,
      birth_date: signupData.birth_date,
      referral_code: signupData.referral_code,
    });

    setLoading(true);
    setSignupError("");
    try {
      const { response, result, data } = await postGoogleLogin(requestPayload);

      if (!response.ok || !data.auth || !data.access || !data.refresh) {
        setSignupError(getErrorText(result));
        return;
      }

      onAuthSuccess(data);
    } catch (error) {
      console.error("Google signup request failed:", error);
      setSignupError("ثبت نام انجام نشد. لطفاً دوباره تلاش کنید.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md bg-zi-bg2-light dark:bg-zi-bg2-dark rounded-2xl p-5 m-4 gap-7 flex flex-col border border-zi-blue-light">
      <div className="flex flex-col gap-2 justify-center items-center">
        <img src={logoSrc} alt="لوگو" className="w-14" />
        <p className="font-medium text-sm text-zi-text-light dark:text-zi-text-dark">
          کوژیتو فاند
        </p>
      </div>
      <div className="flex justify-between items-center">
        <h1 className="text-lg font-medium text-zi-text-light dark:text-zi-text-dark">
          ایجاد حساب کاربری
        </h1>
        <Back onClick={onBack} />
      </div>

      <div>
        <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-5 mb-5">
            <Input
              label="نام"
              isNecessary={true}
              value={signupData.fn || ""}
              type="text"
              id="fn"
              name="fn"
              isReadOnly={false}
              isAuotComplete={true}
              onChange={handleChange("fn")}
            />
            <Input
              label="نام خانوادگی"
              isNecessary={true}
              value={signupData.ln || ""}
              type="text"
              id="ln"
              name="ln"
              isReadOnly={false}
              isAuotComplete={true}
              onChange={handleChange("ln")}
            />
            <Input
              label="کد ملی"
              value={signupData.national_id || ""}
              type="text"
              id="national_id"
              name="national_id"
              isReadOnly={false}
              isAuotComplete={true}
              isLTR={true}
              isNumeric={true}
              onChange={handleChange("national_id")}
            />
            <BirthDatePicker
              label="تاریخ تولد"
              value={signupData.birth_date || ""}
              isReadOnly={false}
              onChange={handleBirthDateChange}
            />
          </div>
          <GradientBlue
            type="submit"
            value="ایجاد"
            isLoader={loading}
            onClick={handleSubmit}
          />
        </form>
        {fieldsRequired && (
          <p className="text-red-500 text-sm font-extralight mt-1">
            لطفا نام و نام خانوادگی را وارد کنید
          </p>
        )}
        {invalidNationalId && (
          <p className="text-red-500 text-sm font-extralight mt-1">
            کد ملی وارد شده صحیح نمی باشد
          </p>
        )}
        {signupError && (
          <p className="text-red-500 text-sm font-extralight mt-1">
            {signupError}
          </p>
        )}
      </div>
    </div>
  );
}

export default function GoogleAuthRedirect() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const credential = useMemo(() => getCredential(search), [search]);
  const urlRef = new URLSearchParams(search).get("ref") || "";
  const [step, setStep] = useState("loading");
  const [error, setError] = useState("");
  const [signupData, setSignupData] = useState({
    ...DEFAULT_SIGNUP_DATA,
    referral_code: urlRef,
  });
  const initialRequestStarted = useRef(false);

  const handleAuthSuccess = useCallback((data) => {
    storeAuthData(data);
    navigate("/dashboard", { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (initialRequestStarted.current) {
      return;
    }
    initialRequestStarted.current = true;

    if (!credential) {
      setError("اطلاعات ورود گوگل دریافت نشد. لطفاً دوباره تلاش کنید.");
      setStep("error");
      return;
    }

    async function loginWithGoogle() {
      setStep("loading");
      setError("");
      try {
        const { response, result, data } = await postGoogleLogin({
          credential,
          credentials: credential,
        });

        if (!response.ok) {
          setError(getErrorText(result));
          setStep("error");
          return;
        }

        if (data.auth && data.twoFa && !data.access) {
          setStep("twoFa");
          return;
        }

        if (data.auth && data.access && data.refresh) {
          handleAuthSuccess(data);
          return;
        }

        if (data.signup_required) {
          setStep("signupPhone");
          return;
        }

        setError(getErrorText(result));
        setStep("error");
      } catch (requestError) {
        console.error("Google login request failed:", requestError);
        setError("ورود با گوگل انجام نشد. لطفاً دوباره تلاش کنید.");
        setStep("error");
      }
    }

    loginWithGoogle();
  }, [credential, handleAuthSuccess]);

  if (step === "loading") {
    return (
      <PageFrame>
        <LoadingCard />
      </PageFrame>
    );
  }

  if (step === "twoFa") {
    return (
      <PageFrame>
        <GoogleTwoFaStep
          credential={credential}
          onAuthSuccess={handleAuthSuccess}
        />
      </PageFrame>
    );
  }

  if (step === "signupPhone") {
    return (
      <PageFrame>
        <GooglePhoneStep
          signupData={signupData}
          setSignupData={setSignupData}
          onNext={() => setStep("signupInfo")}
        />
      </PageFrame>
    );
  }

  if (step === "signupInfo") {
    return (
      <PageFrame>
        <GoogleInfoStep
          credential={credential}
          signupData={signupData}
          setSignupData={setSignupData}
          onBack={() => setStep("signupPhone")}
          onAuthSuccess={handleAuthSuccess}
        />
      </PageFrame>
    );
  }

  return (
    <PageFrame>
      <div className="w-full max-w-md bg-zi-bg2-light dark:bg-zi-bg2-dark rounded-2xl p-5 m-4 gap-5 flex flex-col border border-zi-blue-light">
        <p className="text-red-500 text-sm font-extralight">{error}</p>
        <GradientBlue
          type="button"
          value="بازگشت به ورود"
          onClick={() => navigate("/signin/email", { replace: true })}
        />
      </div>
    </PageFrame>
  );
}
